import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { Discount, DiscountListResponse } from '../../types';
import { getCategories } from '../../data/categoriesStore';
import styles from './Reductions.module.css';

export const Reductions: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const categoryList = useMemo(() => getCategories(), []);

  // État du formulaire
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [code, setCode] = useState<string>('');
  const [type, setType] = useState<string>('percent');
  const [value, setValue] = useState<number>(0);
  const [minOrder, setMinOrder] = useState<number>(0);
  const [active, setActive] = useState<boolean>(true);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [expiresEnabled, setExpiresEnabled] = useState<boolean>(false);
  const [category, setCategory] = useState<string>('');
  const [subCategory, setSubCategory] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [maxUses, setMaxUses] = useState<string>('');
  const [maxItems, setMaxItems] = useState<string>('');

  const subCategoryList = useMemo(
    () => categoryList.find((c) => c.name === category)?.subCategories ?? [],
    [categoryList, category]
  );

  useEffect(() => {
    loadDiscounts();
  }, []);

  const loadDiscounts = async () => {
    try {
      setLoading(true);
      setError('');
      resetForm();

      const response = await api.get<DiscountListResponse>('discount');
      const apiResponse = response.data;

      if (apiResponse.success && apiResponse.data) {
        setDiscounts(apiResponse.data);
      } else {
        setDiscounts([]);
      }
    } catch (err: any) {
      setError('Erreur lors du chargement des réductions : ' + (err.message || 'Erreur inconnue'));
      setDiscounts([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedId(null);
    setCode('');
    setType('percent');
    setValue(0);
    setMinOrder(0);
    setActive(true);
    setExpiresAt('');
    setExpiresEnabled(false);
    setCategory('');
    setSubCategory('');
    setUserId('');
    setMaxUses('');
    setMaxItems('');
  };

  const handleRowClick = (discount: Discount) => {
    setSelectedId(discount.id || null);
    setCode(discount.code ?? '');
    setType(discount.type ?? 'percent');
    setValue(discount.value ?? 0);
    setMinOrder(discount.minOrder ?? 0);
    setActive(Boolean(discount.active));
    setCategory(discount.category ?? '');
    setSubCategory(discount.subCategory ?? '');
    setUserId(discount.userId != null ? String(discount.userId) : '');
    setMaxUses(discount.maxUses != null ? String(discount.maxUses) : '');
    setMaxItems(discount.maxItems != null ? String(discount.maxItems) : '');

    if (discount.expiresAt) {
      setExpiresAt(discount.expiresAt.split('T')[0]);
      setExpiresEnabled(true);
    } else {
      setExpiresAt('');
      setExpiresEnabled(false);
    }
  };

  const validateForm = (): boolean => {
    if (!code.trim()) { alert('Le code promo est obligatoire.'); return false; }
    if (!type) { alert('Veuillez sélectionner un type de réduction.'); return false; }
    if (value <= 0) { alert('La valeur de la réduction doit être supérieure à 0.'); return false; }
    return true;
  };

  const buildDiscountDto = (): Discount => ({
    code: code.trim(),
    type,
    value,
    minOrder,
    active,
    expiresAt: expiresEnabled && expiresAt ? expiresAt : null,
    category: category || null,
    subCategory: subCategory || null,
    userId: userId ? parseInt(userId) : null,
    maxUses: maxUses ? parseInt(maxUses) : null,
    maxItems: maxItems ? parseInt(maxItems) : null,
  });

  const handleAdd = async () => {
    if (!validateForm()) return;
    try {
      await api.post('discount', buildDiscountDto());
      alert('Code promo créé avec succès.');
      loadDiscounts();
    } catch (err: any) {
      alert('Erreur lors de la création : ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async () => {
    if (selectedId === null) { alert('Veuillez sélectionner un code promo à modifier.'); return; }
    if (!validateForm()) return;
    try {
      const dto = { ...buildDiscountDto(), id: selectedId };
      await api.put(`discount/${selectedId}`, dto);
      alert('Code promo mis à jour.');
      loadDiscounts();
    } catch (err: any) {
      alert('Erreur lors de la mise à jour : ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async () => {
    if (selectedId === null) { alert('Veuillez sélectionner un code promo à supprimer.'); return; }
    const selected = discounts.find((d) => d.id === selectedId);
    if (!window.confirm(`Supprimer le code « ${selected?.code} » ?`)) return;
    try {
      await api.delete(`discount/${selectedId}`);
      alert('Code promo supprimé.');
      loadDiscounts();
    } catch (err: any) {
      alert('Erreur lors de la suppression : ' + (err.response?.data?.message || err.message));
    }
  };

  const formatTypeLabel = (t: string) => t === 'percent' ? '% remise' : '€ déduit';

  return (
    <Layout>
      <div className={styles.container}>
        <h1 className={styles.title}>Gestion des codes promo</h1>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.mainLayout}>
          {/* TABLEAU */}
          <div className={styles.tableSection}>
            {loading && discounts.length === 0 ? (
              <div className={styles.loading}>Chargement...</div>
            ) : discounts.length === 0 ? (
              <div className={styles.noData}>Aucune réduction. Créez-en une via le formulaire →</div>
            ) : (
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Type</th>
                      <th>Valeur</th>
                      <th>Min. commande</th>
                      <th>Restriction</th>
                      <th>Utilisations</th>
                      <th>Actif</th>
                      <th>Expire le</th>
                      <th>Créé le</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discounts.map((discount) => (
                      <tr
                        key={discount.id}
                        onClick={() => handleRowClick(discount)}
                        className={`${styles.row} ${selectedId === discount.id ? styles.selectedRow : ''}`}
                      >
                        <td><strong>{discount.code}</strong></td>
                        <td>{formatTypeLabel(discount.type)}</td>
                        <td>{discount.type === 'percent' ? `${discount.value}%` : `${discount.value}€`}</td>
                        <td>{discount.minOrder > 0 ? `${discount.minOrder}€` : '—'}</td>
                        <td>
                          <div style={{ fontSize: '0.8rem', color: '#555', lineHeight: 1.5 }}>
                            {discount.category && <div>{discount.category}{discount.subCategory ? ` › ${discount.subCategory}` : ''}</div>}
                            {discount.userId && <div style={{ color: '#0066cc' }}>Client #{discount.userId}</div>}
                            {!discount.category && !discount.userId && <span style={{ color: '#aaa' }}>—</span>}
                          </div>
                        </td>
                        <td>
                          <span style={{ fontSize: '0.82rem' }}>
                            {discount.usedCount ?? 0}
                            {discount.maxUses != null ? `/${discount.maxUses}` : ''}
                            {discount.maxItems != null && (
                              <span style={{ color: '#888', marginLeft: 4 }}>· {discount.maxItems} art.</span>
                            )}
                          </span>
                        </td>
                        <td>
                          {(() => {
                            const expired = discount.expiresAt && new Date(discount.expiresAt) < new Date();
                            const label = expired ? 'Expiré' : discount.active ? 'Actif' : 'Inactif';
                            const bg = expired ? '#fff3cd' : discount.active ? '#d4edda' : '#f8d7da';
                            const color = expired ? '#856404' : discount.active ? '#155724' : '#721c24';
                            return (
                              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem', fontWeight: 600, background: bg, color }}>
                                {label}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          {discount.expiresAt ? (() => {
                            const expired = new Date(discount.expiresAt) < new Date();
                            return (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                background: expired ? '#f8d7da' : '#fff3cd',
                                color: expired ? '#721c24' : '#856404',
                              }}>
                                {expired ? '⚠ Expiré ' : ''}{new Date(discount.expiresAt).toLocaleDateString('fr-FR')}
                              </span>
                            );
                          })() : '—'}
                        </td>
                        <td>
                          {discount.createdAt
                            ? new Date(discount.createdAt).toLocaleDateString('fr-FR')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* FORMULAIRE */}
          <div className={styles.formSection}>
            <h2 className={styles.formTitle}>
              {selectedId ? `Modifier — ${code || '…'}` : 'Nouveau code promo'}
            </h2>

            {selectedId && (
              <button onClick={resetForm} className={styles.btnReset}>
                ✕ Désélectionner
              </button>
            )}

            <div className={styles.formGroup}>
              <label className={styles.label}>Code promo *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className={styles.input}
                  placeholder="EX: PROMO10"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                    const random = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
                    setCode(random);
                  }}
                  className={styles.btnReset}
                  title="Générer un code aléatoire"
                >
                  🎲 Aléatoire
                </button>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)} className={styles.select}>
                <option value="percent">% — Pourcentage</option>
                <option value="amount">€ — Montant fixe</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Valeur {type === 'percent' ? '(%)' : '(€)'} *</label>
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Restreindre à une catégorie</label>
              <select
                value={category}
                onChange={(e) => { setCategory(e.target.value); setSubCategory(''); }}
                className={styles.select}
              >
                <option value="">— Toutes catégories —</option>
                {categoryList.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>

            {category && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Restreindre à une sous-catégorie</label>
                <select
                  value={subCategory}
                  onChange={(e) => setSubCategory(e.target.value)}
                  className={styles.select}
                >
                  <option value="">— Toutes sous-catégories de {category} —</option>
                  {subCategoryList.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            <div className={styles.formGroup}>
              <label className={styles.label}>Restreindre à un client (ID)</label>
              <input
                type="number"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className={styles.input}
                placeholder="Laisser vide = tout le monde"
                min="1"
              />
              <small style={{ color: '#888', fontSize: '0.75rem' }}>
                L'ID du client se trouve dans la page Comptes
              </small>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Nb max d'utilisations (vide = illimité)</label>
              <input
                type="number"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                className={styles.input}
                placeholder="Ex: 1 = usage unique"
                min="1"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Nb max de produits concernés (vide = tous)</label>
              <input
                type="number"
                value={maxItems}
                onChange={(e) => setMaxItems(e.target.value)}
                className={styles.input}
                placeholder="Ex: 1 = 1 seul article offert"
                min="1"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Commande minimum (€)</label>
              <input
                type="number"
                value={minOrder}
                onChange={(e) => setMinOrder(parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                className={styles.input}
                placeholder="0 = pas de minimum"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className={styles.checkbox}
                />
                Actif
              </label>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Date d'expiration</label>
              <div className={styles.dateGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={expiresEnabled}
                    onChange={(e) => setExpiresEnabled(e.target.checked)}
                    className={styles.checkbox}
                  />
                  Activer une date limite
                </label>
                {expiresEnabled && (
                  <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className={styles.input}
                  />
                )}
              </div>
            </div>

            <div className={styles.actions}>
              {!selectedId ? (
                <button onClick={handleAdd} className={styles.btnAdd}>+ Ajouter</button>
              ) : (
                <>
                  <button onClick={handleUpdate} className={styles.btnUpdate}>Sauvegarder</button>
                  <button onClick={handleDelete} className={styles.btnDelete}>Supprimer</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
